import React, { useEffect, useRef, useState } from "react";
import { api, apiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Loader2 } from "lucide-react";

const TYPE_LABELS = {
  before: "Prima",
  after: "Dopo",
  front: "Fronte",
  side: "Lato",
  back: "Retro",
};

const TYPE_COLORS = {
  before: "#F59E0B",
  after: "#2F9E77",
  front: "#3B82F6",
  side: "#0EA5E9",
  back: "#7C3AED",
};

/**
 * PhotoUploader – aggiunge/elenca/rimuove foto Pre/Post per un appuntamento.
 * Usa <input type="file" capture="environment"> per attivare la fotocamera su mobile.
 * Le foto vengono ridotte a max 1024px lato lungo + JPEG 70% per contenere il payload.
 */
export default function PhotoUploader({ appointmentId, disabled, onCountChange }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("before");
  const [preview, setPreview] = useState(null); // photo obj for lightbox
  const fileRef = useRef(null);

  const load = async () => {
    if (!appointmentId) return;
    try {
      const r = await api.get(`/appointments/${appointmentId}/photos`);
      setPhotos(Array.isArray(r.data) ? r.data : []);
      onCountChange?.((r.data || []).length);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, [appointmentId]);

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1024;
        let w = img.width, h = img.height;
        if (w > h && w > maxDim) { h = h * (maxDim / w); w = maxDim; }
        else if (h > maxDim) { w = w * (maxDim / h); h = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(w); canvas.height = Math.round(h);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const pickFile = () => {
    if (disabled || !appointmentId) {
      toast.error("Salva prima l'appuntamento per aggiungere foto");
      return;
    }
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // consenti stesso file più volte
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Formato non supportato");
    setLoading(true);
    try {
      const dataUrl = await compressImage(file);
      await api.post(`/appointments/${appointmentId}/photos`, { type: selectedType, data_url: dataUrl });
      toast.success(`Foto ${TYPE_LABELS[selectedType]} salvata`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento foto");
    } finally {
      setLoading(false);
    }
  };

  const del = async (pid) => {
    if (!window.confirm("Eliminare la foto?")) return;
    try {
      await api.delete(`/appointments/${appointmentId}/photos/${pid}`);
      toast.success("Foto eliminata");
      await load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const openPreview = async (p) => {
    try {
      const r = await api.get(`/appointments/${appointmentId}/photos/${p.id}`);
      setPreview(r.data);
    } catch { toast.error("Errore caricamento anteprima"); }
  };

  return (
    <div data-testid="photo-uploader">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">
          Foto animale ({photos.length}/8)
        </div>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {Object.entries(TYPE_LABELS).map(([k, label]) => (
            <button
              key={k}
              data-testid={`photo-type-${k}`}
              onClick={() => setSelectedType(k)}
              className={`text-[10px] font-mono uppercase px-2 h-6 rounded border transition ${selectedType === k ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border text-velta-mute hover:border-velta-ink"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {(Array.isArray(photos) ? photos : []).map((p) => (
          <div key={p.id} className="relative group aspect-square bg-velta-bg border border-velta-border rounded-md overflow-hidden">
            <button
              data-testid={`photo-thumb-${p.id}`}
              onClick={() => openPreview(p)}
              className="w-full h-full flex items-center justify-center"
            >
              <img
                src={`${apiUrl(`/public/pet-photo/${appointmentId}/${p.id}`)}`}
                alt={p.type}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
            <span
              className="absolute top-1 left-1 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded text-white font-bold"
              style={{ background: TYPE_COLORS[p.type] || "#0A0A0A" }}
            >
              {TYPE_LABELS[p.type] || p.type}
            </span>
            <button
              data-testid={`photo-del-${p.id}`}
              onClick={() => del(p.id)}
              className="absolute top-1 right-1 h-5 w-5 bg-black/60 text-white rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          data-testid="photo-add-btn"
          onClick={pickFile}
          disabled={disabled || loading || photos.length >= 8}
          className="aspect-square border-2 border-dashed border-velta-border hover:border-velta-ink rounded-md flex flex-col items-center justify-center text-velta-mute hover:text-velta-ink text-[10px] disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>
              <Camera className="w-5 h-5 mb-1" />
              <span className="font-mono uppercase">{TYPE_LABELS[selectedType]}</span>
            </>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFile}
          className="hidden"
          data-testid="photo-file-input"
        />
      </div>

      {/* Lightbox */}
      {preview && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="photo-preview-close"
              onClick={() => setPreview(null)}
              className="absolute -top-10 right-0 text-white hover:text-red-400"
            ><X className="w-6 h-6" /></button>
            <div className="text-center mb-2 text-white text-sm font-mono uppercase">
              <span className="px-2 py-0.5 rounded" style={{ background: TYPE_COLORS[preview.type] }}>{TYPE_LABELS[preview.type]}</span>
              <span className="ml-3 text-white/60">{new Date(preview.created_at).toLocaleString("it-IT")}</span>
            </div>
            <img src={preview.data_url} alt={preview.type} className="w-full max-h-[80vh] object-contain rounded-md" />
          </div>
        </div>
      )}
    </div>
  );
}
